import {faPlus} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Group, Text} from '@mantine/core';
import {FC, ReactNode, useMemo} from 'react';
import {Link} from 'react-router-dom';
import {Roles} from '../api/generated/enums';
import {RepairDocumentSummaryDto} from '../api/generated/index.defs';
import {RepairDocumentsService} from '../api/generated/RepairDocumentsService';
import {useRequireAuthorization} from '../auth/require-authorization';
import {BasicPage} from '../components/basic-page';
import {EditButton} from '../hooks/paged-data-table/row-buttons';
import {ColumnConfig} from '../hooks/paged-data-table/types';
import {
  PagedDataTableConfig,
  usePagedDataTable,
} from '../hooks/paged-data-table/use-paged-data-table';
import {ROUTES} from '../routes';
import {renderedDateFormat} from '../utils/luxon.ts';
import {AnchorIcon} from '../components/anchor-icon.tsx';

const GlobalAdminRoles = ['Global Admin'] as Roles[];

type RepairDocumentsListingProps = {
  children?: ReactNode;
};

export const RepairDocumentsListing: FC<RepairDocumentsListingProps> = () => {
  const isAuthorized = useRequireAuthorization({
    role: GlobalAdminRoles,
  });

  const TableConfig = useTableConfig({isAuthorized});
  const pagedDataTable = usePagedDataTable(
    RepairDocumentsService.getAll,
    TableConfig,
    {
      renderActions: (
        <>
          {isAuthorized && (
            <Button
              component={Link}
              to={ROUTES.ADMIN.REPAIR_DOCUMENTS.CREATE.path}
            >
              <Group gap={'xs'}>
                <FontAwesomeIcon icon={faPlus} /> New Repair Document
              </Group>
            </Button>
          )}
        </>
      ),
    },
  );

  return (
    <BasicPage maxWidth='100%' title='Repair Documentation'>
      {pagedDataTable}
    </BasicPage>
  );
};

const castItemId = (item: RepairDocumentSummaryDto) => ({
  ...item,
  id: Number(item.id),
});

const useTableConfig = ({isAuthorized}) => {
  const columns = useMemo(
    () =>
      [
        {
          accessor: '',
          title: '',
          render: (item) => (
            <>
              {isAuthorized ? (
                <EditButton
                  item={castItemId(item)}
                  descriptor='Repair Document'
                  route={ROUTES.ADMIN.REPAIR_DOCUMENTS.DETAIL.path}
                />
              ) : null}
            </>
          ),
          width: 50,
        },
        {
          accessor: 'id',
          title: 'ID',
        },
        {
          accessor: 'name',
          sortable: true,
          title: 'Name',
          width: 250,
          render: (item) => (
            <Text truncate='end' fz='sm'>
              {item.name}
            </Text>
          ),
        },
        {
          accessor: 'fileName',
          sortable: true,
          title: 'File Name',
          width: 350,
          render: (item) => {
            return (
              <AnchorIcon
                href={item.url}
                target='_blank'
                rel='noopener noreferrer'
              >
                {item.fileName}
              </AnchorIcon>
            );
          },
        },
        {
          accessor: 'description',
          sortable: true,
        },
        {
          accessor: 'type',
          title: 'Type',
          sortable: true,
        },
        {
          accessor: 'createdDate',
          title: 'Date Entered',
          sortable: true,

          render: (item) => renderedDateFormat(item.createdDate),
        },
        {
          accessor: 'sortOrder',
          title: 'Sort Order',
          sortable: true,
          width: 100,
        },
      ] satisfies ColumnConfig<RepairDocumentSummaryDto>[],
    [isAuthorized],
  );

  return useMemo(() => {
    return PagedDataTableConfig(RepairDocumentsService.getAll, {
      columns: columns,
      defaultSort: {
        column: 'createdDate',
        direction: 'DESC',
      },
      searchFieldNames: ['name', 'fileName', 'description', 'type'],
    });
  }, [columns]);
};
